import React from 'react';
import { PositionRecommendation } from '../types';

interface RecommendationItemProps {
  recommendation: PositionRecommendation;
  getRankIcon: (rank: number) => React.ReactNode;
  getRankColor: (rank: number) => string;
  getPriorityColor: (priority: string) => string;
}

const RecommendationItem: React.FC<RecommendationItemProps> = ({
  recommendation,
  getRankIcon,
  getRankColor,
  getPriorityColor,
}) => {
  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-center space-x-3 mb-3">
        {/* Ranking Badge */}
        <div className="flex items-center space-x-2">
          {getRankIcon(recommendation.rank || 0)}
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${getRankColor(recommendation.rank || 0)}`}
          >
            Rank {recommendation.rank} of {recommendation.totalRecommendations}
          </span>
        </div>

        <span className="text-xl font-bold text-gray-900">
          {recommendation.symbol}
        </span>

        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(recommendation.priority)}`}
        >
          {recommendation.priority}
        </span>

        <span className="text-lg font-bold text-blue-600">
          Score: {recommendation.recommendationScore}
        </span>
      </div>

      {/* Score Breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <div className="text-center">
          <p className="text-xs text-gray-500">Timing</p>
          <p className="text-sm font-semibold">
            {recommendation.marketTimingScore}
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-500">Alignment</p>
          <p className="text-sm font-semibold">
            {recommendation.objectiveAlignmentScore}
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-500">Technical</p>
          <p className="text-sm font-semibold">
            {recommendation.technicalAnalysisScore}
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-500">Fundamental</p>
          <p className="text-sm font-semibold">
            {recommendation.fundamentalAnalysisScore}
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-500">Social</p>
          <p className="text-sm font-semibold">
            {recommendation.sentimentAnalysisScore}
          </p>
        </div>
      </div>

      {/* AI Reasoning */}
      <div className="mb-4">
        <p className="text-sm text-gray-500 mb-2">AI Reasoning</p>
        <p className="text-sm text-gray-700 bg-white p-3 rounded border">
          {recommendation.aiReasoning}
        </p>
      </div>

      {/* Key Strengths and Risks */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p className="text-sm text-gray-500 mb-2">Key Strengths</p>
          <ul className="space-y-1">
            {recommendation.keyStrengths.map((strength, index) => (
              <li
                key={index}
                className="text-sm text-gray-700 flex items-start"
              >
                <span className="text-green-500 mr-2">•</span>
                {strength}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-sm text-gray-500 mb-2">Key Risks</p>
          <ul className="space-y-1">
            {recommendation.keyRisks.map((risk, index) => (
              <li
                key={index}
                className="text-sm text-gray-700 flex items-start"
              >
                <span className="text-red-500 mr-2">•</span>
                {risk}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Metadata */}
      <div className="flex items-center text-sm text-gray-500 space-x-4 mt-4">
        <span>
          Created: {new Date(recommendation.createdAt).toLocaleString()}
        </span>
        {recommendation.expiresAt && (
          <span>
            Expires: {new Date(recommendation.expiresAt).toLocaleString()}
          </span>
        )}
      </div>
    </div>
  );
};

export default RecommendationItem;
